import React from "react";
import localforage from "localforage";
import {
  cancelTopup,
  createTopup,
  getTopup,
  isTransientNegotiationFailure,
  newIdempotencyKey,
  type NegotiationBalanceFailure,
  type NegotiationTopup,
  type NegotiationTopupLimits,
  type NegotiationTopupOrigin,
} from "../../services/negotiationBalance";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import {
  isTerminalTopupState,
  LATE_RECEIPT_POLL_MS,
  lateReceiptDeadline,
  parseWholeReais,
  RECHARGE_POLL_MS,
} from "./negotiationBalanceFormat";

export type TopupFlowOptions = {
  origin: NegotiationTopupOrigin;
  initialAmountBRL?: number;
  /**
   * Follow a top-up that already exists (a QR left open earlier) instead of
   * creating one: it is read at once and then polled like a new one.
   */
  resumeTopupId?: string;
  limits: NegotiationTopupLimits;
  /**
   * Payments answered negotiation_terms_required: the acceptance comes first.
   * It receives the amount of the refused attempt (whole reais), so the
   * recharge can resume with it after the acceptance.
   */
  onTermsRequired: (amountBRL: number) => void;
  /** Payments answered negotiation_topup_limit: the list of open QRs may be stale. */
  onTopupLimit?: () => void;
  pollMs?: number;
};

export type TopupFlowState = {
  draft: string;
  topup?: NegotiationTopup;
  /** An existing top-up is being read before it can be shown. */
  resuming: boolean;
  creating: boolean;
  cancelling: boolean;
  cancelRequested: boolean;
  /** A poll failed; another one is scheduled. */
  pollingIssue: boolean;
  /** Generation is still pending after the bounded automatic observation. */
  generationDelayed: boolean;
  /** A GET of the current top-up is in flight, including a manual refresh. */
  checking: boolean;
  /**
   * Set while the QR is expired or removed: until this instant (epoch ms) a
   * payment made at the deadline can still be credited, so polling goes on
   * and a new QR is offered with a warning.
   */
  lateReceiptUntilMs?: number;
  /** Application-authored copy only. */
  error?: string;
};

export type TopupFlow = TopupFlowState & {
  setDraft: (draft: string) => void;
  generate: () => void;
  cancel: () => void;
  /** Read the same delayed top-up once, without creating or cancelling it. */
  refresh: () => void;
  /** Back to the amount form after an expired, removed or abandoned QR. */
  startOver: () => void;
};

const MAX_POLL_BACKOFF_MS = 30_000;
const GENERATION_WAIT_MS = 30_000;

export const SECURE_KEY_COPY =
  "Para gerar o Pix com segurança, use um navegador atualizado em uma conexão segura.";

export function amountRangeCopy(limits: NegotiationTopupLimits): string {
  return `Informe o valor para o saldo, inteiro em reais, de ${formatBRL(limits.minTopupCents)} a ${formatBRL(limits.maxTopupCents)}.`;
}

/** The answer of a refused creation, as the company should read it. */
export function createFailureCopy(
  failure: NegotiationBalanceFailure,
  limits: NegotiationTopupLimits,
): string {
  switch (failure.code) {
    case "negotiation_topup_limit":
      return "Há recargas em aberto; conclua ou cancele uma antes.";
    case "negotiation_fiscal_profile_missing":
      return "Complete o CNPJ e a razão social da empresa.";
    case "invalid_topup_amount":
      return amountRangeCopy(limits);
    case "negotiation_topups_disabled":
      return "As recargas do Saldo de Negociações estão temporariamente indisponíveis.";
    case "negotiation_account_missing":
    case "negotiation_account_closed":
      return "O Saldo de Negociações desta empresa não está ativo.";
    case "rate_limited":
      return "Recebemos muitas recargas em pouco tempo. Aguarde um minuto e tente de novo.";
    case "unauthorized":
      return "Sua sessão expirou. Entre novamente para continuar.";
    case "forbidden":
      return "Seu cargo não permite gerar recargas do Saldo de Negociações.";
    default:
      return isTransientNegotiationFailure(failure)
        ? "Não foi possível gerar o Pix agora. Tente de novo: o mesmo pedido será reenviado."
        : "Não foi possível gerar o Pix. Feche e abra a recarga de novo.";
  }
}

async function readJWT(): Promise<string | undefined> {
  try {
    return (await localforage.getItem<string>("JWT")) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * The life of one recharge QR: create (one Idempotency-Key per attempt,
 * reused only when the same amount is retried after an uncertain answer),
 * then GET /topups/:id every `pollMs` until a terminal state. Generation that
 * remains pending after 30 seconds pauses for a same-ID manual read. Failed
 * polls use a bounded backoff. An expired or removed QR is still watched, more
 * slowly, until its late-receipt deadline, because Payments credits a receipt
 * it proves late. Nothing keeps running after the modal closes.
 */
export function useTopupFlow({
  origin,
  initialAmountBRL,
  resumeTopupId,
  limits,
  onTermsRequired,
  onTopupLimit,
  pollMs = RECHARGE_POLL_MS,
}: TopupFlowOptions): TopupFlow {
  const [state, setState] = React.useState<TopupFlowState>({
    draft: initialAmountBRL && initialAmountBRL > 0 ? String(initialAmountBRL) : "",
    resuming: Boolean(resumeTopupId),
    creating: false,
    cancelling: false,
    cancelRequested: false,
    pollingIssue: false,
    generationDelayed: false,
    checking: false,
  });
  const aliveRef = React.useRef(true);
  const attemptRef = React.useRef<{ key: string; netCents: number } | null>(null);
  // A resumed top-up is the flow's QR from the start, so generate() stays off.
  const topupIdRef = React.useRef<string | undefined>(resumeTopupId);
  const resumeRef = React.useRef(resumeTopupId);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const generationTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const generationRef = React.useRef<{ id: string; deadline: number; delayed: boolean }>();
  const readingIdRef = React.useRef<string>();
  const readRef = React.useRef<
    (id: string, failures: number, baseMs: number, manual: boolean) => Promise<void>
  >(async () => {});
  const lateUntilRef = React.useRef<number | undefined>(undefined);
  const followRef = React.useRef<(topup: NegotiationTopup) => void>(() => {});
  // The id of the top-up last shown, so a resume that never read it can fall back.
  const shownIdRef = React.useRef<string | undefined>(undefined);
  const busyRef = React.useRef(false);
  const termsRef = React.useRef(onTermsRequired);
  termsRef.current = onTermsRequired;
  const limitRef = React.useRef(onTopupLimit);
  limitRef.current = onTopupLimit;

  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearTimeout(timerRef.current);
      clearTimeout(generationTimerRef.current);
    };
  }, []);

  const schedule = React.useCallback(
    (id: string, failures: number, baseMs: number = pollMs, delayMs?: number) => {
      clearTimeout(timerRef.current);
      if (generationRef.current?.id === id && generationRef.current.delayed) return;
      const delay =
        delayMs ??
        (failures === 0 ? baseMs : Math.min(baseMs * 2 ** failures, MAX_POLL_BACKOFF_MS));
      timerRef.current = setTimeout(() => {
        void readRef.current(id, failures, baseMs, false);
      }, delay);
    },
    [pollMs],
  );

  const read = React.useCallback(
    async (id: string, failures: number, baseMs: number, manual: boolean) => {
      if (!aliveRef.current || topupIdRef.current !== id || readingIdRef.current === id) return;
      if (!manual && generationRef.current?.id === id && generationRef.current.delayed) return;
      readingIdRef.current = id;
      setState((previous) => ({
        ...previous,
        checking: true,
        error: manual ? undefined : previous.error,
      }));
      const current = () => aliveRef.current && topupIdRef.current === id;
      const failed = (message: string) => {
        if (!current()) return;
        const paused = generationRef.current?.id === id && generationRef.current.delayed;
        setState((previous) => ({
          ...previous,
          pollingIssue: !manual && !paused,
          error: manual || paused ? message : previous.error,
        }));
        // A paused generation and a finished late-receipt watch never restart.
        const lateOver = lateUntilRef.current !== undefined && Date.now() >= lateUntilRef.current;
        if (!manual && !lateOver) schedule(id, failures + 1, baseMs);
      };
      try {
        const jwt = await readJWT();
        if (!current()) return;
        if (!jwt) {
          failed("Sua sessão expirou. Entre novamente para consultar esta recarga.");
          return;
        }
        const result = await getTopup(jwt, id);
        if (!current()) return;
        if (result.ok) {
          if (result.value.id === id) followRef.current(result.value);
          else failed("Não foi possível atualizar esta recarga. O pedido continua salvo.");
        } else if (result.failure.code === "route_missing") {
          // A resumed QR this session cannot read (another company's id, or
          // a build without the route) was never shown: free the amount form.
          if (shownIdRef.current !== id) topupIdRef.current = undefined;
          setState((previous) => ({
            ...previous,
            resuming: false,
            checking: false,
            pollingIssue: false,
            error: "Não foi possível acompanhar esta recarga aqui. Confira o histórico do saldo.",
          }));
        } else {
          failed(
            result.failure.code === "unauthorized"
              ? "Sua sessão expirou. Entre novamente para consultar esta recarga."
              : result.failure.code === "forbidden"
                ? "Seu cargo não permite consultar esta recarga. O pedido continua salvo."
                : "Não foi possível atualizar a situação. O pedido continua salvo; tente consultar novamente.",
          );
        }
      } catch {
        failed(
          "Não foi possível atualizar a situação. O pedido continua salvo; tente consultar novamente.",
        );
      } finally {
        if (readingIdRef.current === id) {
          readingIdRef.current = undefined;
          if (current()) setState((previous) => ({ ...previous, checking: false }));
        }
      }
    },
    [schedule],
  );
  readRef.current = read;

  // Shows the latest answer and decides the next poll: the normal cadence
  // while the QR is open, the late-receipt one after it expired or was
  // removed, none once nothing can change any more.
  const follow = React.useCallback(
    (topup: NegotiationTopup) => {
      const generating = topup.state === "requested" || topup.state === "put_unknown";
      let generationDelayed = false;
      if (generating) {
        if (generationRef.current?.id !== topup.id) {
          clearTimeout(generationTimerRef.current);
          const now = Date.now();
          const created = Date.parse(topup.createdAt);
          const deadline = Math.min(
            Number.isFinite(created) ? created + GENERATION_WAIT_MS : now + GENERATION_WAIT_MS,
            now + GENERATION_WAIT_MS,
          );
          const generation = { id: topup.id, deadline, delayed: now >= deadline };
          generationRef.current = generation;
          if (!generation.delayed) {
            generationTimerRef.current = setTimeout(() => {
              if (
                !aliveRef.current ||
                topupIdRef.current !== topup.id ||
                generationRef.current !== generation
              )
                return;
              generation.delayed = true;
              clearTimeout(timerRef.current);
              setState((previous) => ({
                ...previous,
                generationDelayed: true,
                pollingIssue: false,
              }));
            }, deadline - now);
          }
        }
        generationDelayed = Boolean(generationRef.current?.delayed);
      } else {
        clearTimeout(generationTimerRef.current);
        generationRef.current = undefined;
      }
      let lateReceiptUntilMs: number | undefined;
      if (topup.state === "expired" || topup.state === "removed") {
        if (lateUntilRef.current === undefined)
          lateUntilRef.current = lateReceiptDeadline(topup.expiresAt, Date.now());
        lateReceiptUntilMs = lateUntilRef.current;
      } else {
        lateUntilRef.current = undefined;
      }
      shownIdRef.current = topup.id;
      setState((previous) => ({
        ...previous,
        topup,
        resuming: false,
        pollingIssue: false,
        generationDelayed,
        lateReceiptUntilMs,
      }));
      if (generationDelayed) clearTimeout(timerRef.current);
      else if (!isTerminalTopupState(topup.state)) schedule(topup.id, 0);
      else if (lateReceiptUntilMs !== undefined && Date.now() < lateReceiptUntilMs)
        schedule(topup.id, 0, LATE_RECEIPT_POLL_MS);
    },
    [schedule],
  );
  followRef.current = follow;

  // A resumed top-up is read right away, then followed like a new one.
  React.useEffect(() => {
    const id = resumeRef.current;
    if (id && topupIdRef.current === id) schedule(id, 0, pollMs, 0);
  }, [schedule, pollMs]);

  const adopt = React.useCallback(
    (topup: NegotiationTopup) => {
      topupIdRef.current = topup.id;
      lateUntilRef.current = undefined;
      setState((previous) => ({
        ...previous,
        creating: false,
        cancelling: false,
        cancelRequested: false,
        error: undefined,
      }));
      follow(topup);
    },
    [follow],
  );

  const generate = React.useCallback(() => {
    if (busyRef.current || topupIdRef.current) return;
    // The draft is the balance the company wants (the net); Payments adds the
    // top-up fee to the QR it creates.
    const reais = parseWholeReais(state.draft);
    const netCents = reais === null ? null : reais * 100;
    if (
      netCents === null ||
      !Number.isSafeInteger(netCents) ||
      netCents < limits.minTopupCents ||
      netCents > limits.maxTopupCents
    ) {
      setState((previous) => ({ ...previous, error: amountRangeCopy(limits) }));
      return;
    }
    // A new amount is a new attempt. The same amount after an uncertain answer
    // replays the same key, so Payments returns the QR it may already have made.
    if (!attemptRef.current || attemptRef.current.netCents !== netCents) {
      let key: string;
      try {
        key = newIdempotencyKey();
      } catch {
        // No secure random source: a top-up without a stable key could mint two QRs.
        setState((previous) => ({ ...previous, error: SECURE_KEY_COPY }));
        return;
      }
      attemptRef.current = { key, netCents };
    }
    const attempt = attemptRef.current;
    busyRef.current = true;
    setState((previous) => ({ ...previous, creating: true, error: undefined }));
    void (async () => {
      try {
        const jwt = await readJWT();
        if (!aliveRef.current) return;
        if (!jwt) {
          setState((previous) => ({
            ...previous,
            creating: false,
            error: "Sua sessão expirou. Entre novamente para continuar.",
          }));
          return;
        }
        const result = await createTopup(jwt, {
          idempotencyKey: attempt.key,
          netCents: attempt.netCents,
          origin,
        });
        if (!aliveRef.current) return;
        if (result.ok) {
          attemptRef.current = null;
          adopt(result.value);
          return;
        }
        if (!isTransientNegotiationFailure(result.failure)) attemptRef.current = null;
        if (result.failure.code === "negotiation_topup_limit") limitRef.current?.();
        if (result.failure.code === "negotiation_terms_required") {
          setState((previous) => ({ ...previous, creating: false }));
          termsRef.current(attempt.netCents / 100);
          return;
        }
        setState((previous) => ({
          ...previous,
          creating: false,
          error: createFailureCopy(result.failure, limits),
        }));
      } finally {
        busyRef.current = false;
      }
    })();
  }, [adopt, limits, origin, state.draft]);

  const cancel = React.useCallback(() => {
    const id = topupIdRef.current;
    if (!id || busyRef.current) return;
    busyRef.current = true;
    setState((previous) => ({ ...previous, cancelling: true, error: undefined }));
    void (async () => {
      try {
        const jwt = await readJWT();
        if (!aliveRef.current || topupIdRef.current !== id) return;
        const result = jwt ? await cancelTopup(jwt, id) : undefined;
        if (!aliveRef.current || topupIdRef.current !== id) return;
        if (result?.ok) {
          setState((previous) => ({ ...previous, cancelling: false, cancelRequested: true }));
          follow(result.value);
          return;
        }
        setState((previous) => ({
          ...previous,
          cancelling: false,
          error: "Não foi possível cancelar agora. Se o Pix já foi pago, o crédito ainda entra.",
        }));
      } finally {
        busyRef.current = false;
      }
    })();
  }, [follow]);

  const refresh = React.useCallback(() => {
    const id = topupIdRef.current;
    if (!id || generationRef.current?.id !== id || !generationRef.current.delayed) return;
    clearTimeout(timerRef.current);
    void readRef.current(id, 0, pollMs, true);
  }, [pollMs]);

  const startOver = React.useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(generationTimerRef.current);
    generationRef.current = undefined;
    topupIdRef.current = undefined;
    shownIdRef.current = undefined;
    lateUntilRef.current = undefined;
    attemptRef.current = null;
    setState((previous) => ({
      draft: previous.draft,
      resuming: false,
      creating: false,
      cancelling: false,
      cancelRequested: false,
      pollingIssue: false,
      generationDelayed: false,
      checking: false,
    }));
  }, []);

  const setDraft = React.useCallback((draft: string) => {
    setState((previous) => ({ ...previous, draft, error: undefined }));
  }, []);

  return { ...state, setDraft, generate, cancel, refresh, startOver };
}
